<template>
  <div class="promocode-wrapper d-flex fl-col fl-j-c fl-a-c">
    <div class="w-full d-flex fl-j-c fl-a-c options-wrapper gap-2">
      <SButton
        type="button"
        size="lg"
        class="flex-1 h-14"
        :variant="
          paymentType === PaymentMethods.POS_PROMO_CODE ? 'default' : 'outline'
        "
        @click="updatePaymentType(PaymentMethods.POS_PROMO_CODE)"
      >
        {{ $t("promocode.promocode") }}
      </SButton>
      <SButton
        type="button"
        size="lg"
        class="flex-1 h-14"
        :variant="
          paymentType === PaymentMethods.POS_GIFT_CARD ? 'default' : 'outline'
        "
        @click="updatePaymentType(PaymentMethods.POS_GIFT_CARD)"
      >
        {{ $t("promocode.gift-card") }}
      </SButton>
      <SButton
        type="button"
        size="lg"
        class="flex-1 h-14"
        :variant="
          paymentType === PaymentMethods.POS_MEMBERSHIP ? 'default' : 'outline'
        "
        @click="updatePaymentType(PaymentMethods.POS_MEMBERSHIP)"
      >
        {{ $t("promocode.membership") }}
      </SButton>
    </div>
    <div class="inputs-wrapper">
      <button
        class="el-button booking-button scan-promocode-button"
        @click="openScanPromocodeModal"
      >
        <div class="d-flex fl-s-b fl-a-c">
          <p>{{ $t("button.scan-voucher") }}</p>
          <ScanQrCodeIcon
            style="--s-icon-size: 40px"
            aria-label="Scanner icon"
          />
        </div>
      </button>
      <div class="scanner-divider">
        <SSeparator />
      </div>

      <div class="mx-auto flex w-[96%] gap-2.5">
        <SInputGroup class="h-20 min-w-0 flex-1">
          <SInputGroupInput
            class="h-full text-[26px]"
            :placeholder="inputPlaceholder"
            :model-value="promocode"
            @update:model-value="(v) => (promocode = String(v).toUpperCase())"
            data-cy="promocode-input"
          />
          <SInputGroupAddon align="inline-end">
            <SvgIcon
              :iconContent="editPenIconContent"
              :iconAlt="editPenIconAlt"
              :iconStyle="editPenIconStyle"
            />
          </SInputGroupAddon>
        </SInputGroup>
        <SButton
          type="button"
          class="h-20 w-[30%] shrink-0 text-3xl"
          :loading="isLoading"
          :disabled="!isPromocodeLengthValid"
          @click="applyPromocode"
          data-cy="apply-promocode-button"
        >
          {{ $t("promocode.apply") }}
        </SButton>
      </div>

      <div
        style="
          margin-top: 10px;
          width: 96%;
          margin-left: auto;
          margin-right: auto;
        "
      >
        <div
          class="codes-container"
          style="display: flex; flex-direction: row; flex-wrap: wrap"
        >
          <div
            v-for="stat in appliedCodesStats"
            :key="stat.code"
            class="one-code-container d-flex fl-col fl-j-c"
            style="
              width: 47%;
              margin-bottom: 5px;
              margin-top: 5px;
              margin-right: 2%;
            "
          >
            <div
              class="d-flex flex-row fl-j-c"
              style="
                height: 45px;
                line-height: 45px;
                background-color: #efefef;
                display: flex;
                flex-direction: row;
                justify-content: space-between;
              "
            >
              <div style="text-align: center; width: 80%">
                <p>
                  {{ stat.code }}
                </p>
              </div>
              <div
                style="
                  cursor: pointer;
                  text-align: center;
                  width: 15%;
                  font-size: 25px;
                "
                data-cy="remove-promocode-btn"
                @click="removePromocode(stat.orderDiscountId)"
              >
                <p>X</p>
              </div>
            </div>
            <div class="code-results d-flex fl-col fl-j-c">
              <div style="margin-top: 5px">
                <p>
                  {{ -stat.ttlDiscountOrPayment.toFixed(2) + " " + currency }}
                </p>
              </div>
              <div style="margin-top: 5px">
                <p v-if="Number(stat.remaining) >= 0">
                  {{ t("promocode.remaining-amount") }}
                  {{ Number(stat.remaining).toFixed(2) + " " + currency }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <SDialog v-model:open="scanPromocodeModal">
      <SDialogContent
        class="promocode-wrapper scan-promocode-modal !w-[620px] !max-w-[620px]"
        @open-auto-focus.prevent
      >
        <SDialogHeader>
          <SDialogTitle class="sr-only">
            {{ $t("promocode.please-scan-voucher") }}
          </SDialogTitle>
        </SDialogHeader>
        <div class="content">
          <div class="flex w-full items-center justify-center gap-4">
            <ScanQrCodeIcon
              style="--s-icon-size: 64px"
              aria-label="Scanner icon"
              class="shrink-0"
            />
            <p class="text-base font-semibold text-foreground leading-snug">
              {{ $t("promocode.please-scan-voucher") }}
            </p>
          </div>
        </div>
      </SDialogContent>
    </SDialog>
  </div>
</template>
<script lang="ts">
import { computed, defineComponent, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import NotificationService from "@/services/notification.service";
import { LogLevel, LogService } from "@/services/logging";
import editPen from "@/assets/images/svg/edit-pen-rectangle.svg";
import { Currencies } from "@/constants/currency";
import { PaymentMethods } from "@/types/db/accounting";
import ScannerModule from "@/store/modules/scanner";
import {
  extractCodeFromUrl,
  getAppliedCodePaymentType,
  getPromocodeInputPlaceholder,
} from "@/utils/vue-helpers/promocode";
import { getPssVoucherByToken } from "@/api/gql/product-slot-state";
import { Discount, DiscountType } from "@smeetz/order-common";
import { useOrderStore } from "@smeetz/order-store";
import {
  applyDiscountCode,
  removeDiscount,
} from "@/utils/vue-helpers/orders.helper";
import POSConfigModule from "@/store/modules/pos-config";
import {
  SButton,
  SDialog,
  SDialogContent,
  SDialogHeader,
  SDialogTitle,
  SInputGroup,
  SInputGroupAddon,
  SInputGroupInput,
  SSeparator,
} from "@smeetz/ui-core";
import { ScanQrCodeIcon } from "@smeetz/ui-icons";

export default defineComponent({
  name: "Promocode",
  components: {
    SButton,
    SDialog,
    SDialogContent,
    SDialogHeader,
    SDialogTitle,
    SInputGroup,
    SInputGroupAddon,
    SInputGroupInput,
    SSeparator,
    ScanQrCodeIcon,
  },
  setup() {
    const { t } = useI18n({});
    const editPenIconStyle = "width: 30px; height: 30px";
    const editPenIconContent = editPen;
    const editPenIconAlt = "Edit";

    const orderStore = useOrderStore();

    const promocode = ref(getExistingPromocode());
    const isLoading = ref(false);

    const isPromocodeLengthValid = computed(() => {
      return promocode.value.length >= 5;
    });

    /**
     * Codes applied to the active order: discount-side codes (PROMOCODE,
     * MEMBERSHIP_VOUCHER, …) plus vouchers (gift cards) from
     * `orderSummary.vouchers`. Both render in the codes list below the
     * input — only the input pre-fill (`getExistingPromocode`) excludes
     * vouchers.
     */
    const appliedCodesStats = computed(() => {
      const orderDiscounts = orderStore.selectedOrderSummary.orderDiscounts;
      const vouchers = orderStore.selectedOrderSummary.vouchers;
      // Per-line allocation totals live on `orderLines[].discounts`.
      const discounts: Discount[] =
        orderStore.selectedOrderSummary.orderLines.flatMap(
          (l) => l.discounts ?? []
        );

      type Row = {
        code: string;
        orderDiscountId: string;
        ttlDiscountOrPayment: number;
        remaining: number;
      };

      const rows: Row[] = [];

      // Code-bearing discounts (PROMOCODE, MEMBERSHIP_VOUCHER, …) — gift-card
      // vouchers are excluded here and added below from the vouchers array.
      for (const od of orderDiscounts) {
        if (!od.code || od.type === DiscountType.VOUCHER) continue;
        const ttlDiscountOrPayment = discounts
          .filter((d) => d.orderDiscountId === od.id)
          .reduce((sum, d) => sum + Number(d.value || 0), 0);
        rows.push({
          code: String(od.code),
          orderDiscountId: od.id,
          ttlDiscountOrPayment,
          remaining: Number(od.flatDiscount || 0) - ttlDiscountOrPayment,
        });
      }

      // Voucher rows (gift cards / Smeetz vouchers). `Voucher.remainingValue`
      // is the at-apply-time balance, so we subtract `value` to render the
      // post-apply remaining. Legacy rows without the field pass -1 so the
      // template's `v-if="remaining >= 0"` hides the line.
      // `voucher.id` is what `removeDiscount` expects.
      for (const voucher of vouchers) {
        if (!voucher.code) continue;
        const applied = Number(voucher.value || 0);
        const baseRemaining = voucher.remainingValue;
        rows.push({
          code: String(voucher.code),
          orderDiscountId: voucher.id,
          ttlDiscountOrPayment: applied,
          remaining:
            baseRemaining !== undefined && baseRemaining !== null
              ? Number(baseRemaining) - applied
              : -1,
        });
      }

      return rows;
    });

    const currency = computed(() => {
      return orderStore.selectedOrder?.orderSession.currency || Currencies.CHF;
    });

    const paymentType = ref(PaymentMethods.POS_PROMO_CODE);
    const inputPlaceholder = computed(() =>
      getPromocodeInputPlaceholder(paymentType.value)
    );

    function updatePaymentType(type: PaymentMethods) {
      paymentType.value = type;
    }

    const scanPromocodeModal = ref(false);
    const ScannerTog = computed(() => {
      return ScannerModule.tog;
    });

    watch(
      ScannerTog,
      async () => {
        if (ScannerModule.value && scanPromocodeModal.value) {
          LogService.info(
            `Promocode: scanner triggered, value = ${ScannerModule.value}`
          );
          // The scanned valued can be a json object, a url (old logic), or a simple token
          // Check if the voucherCode is a URL
          const scannedValue = ScannerModule.value;
          const isUrl =
            String(scannedValue).startsWith("http://") ||
            String(scannedValue).startsWith("https://") ||
            String(scannedValue).startsWith("http//") ||
            String(scannedValue).startsWith("https//");

          let code = "";
          if (isUrl) {
            LogService.info(`Promocode: scanned value is in url format`);
            console.log(`Promocode: scanned value is in url format`);
            // We keep the old logic of extracting the code from the url scanned
            code = extractCodeFromUrl(ScannerModule.value);
            LogService.info(
              `Promocode: extracted from qrcode string code: ${code}`
            );
            if (code && code.length >= 5) {
              promocode.value = code;
              LogService.info(
                `Promocode: going to process scanned promocode: ${promocode.value}`
              );
              await applyPromocode();
              LogService.info(
                `Promocode: Done processing scanned promocode: ${promocode.value}`
              );
            }
            scanPromocodeModal.value = false;
            return;
          }

          LogService.info(
            `Promocode: scanned value is not in url format. Checking if it is json object.`
          );

          // Check if the scanned value is actually a json object
          try {
            const scannedJsonCode = JSON.parse(scannedValue);
            LogService.info(
              `Promocode: scanned value is in json format attached.`,
              { params: scannedJsonCode }
            );
            // Check if it's in the expected format
            if (
              scannedJsonCode &&
              scannedJsonCode.code &&
              scannedJsonCode.token
            ) {
              LogService.info(
                `Promocode: scanned value is exactly in the expected format.`,
                { params: scannedJsonCode }
              );
              // Lets proceed applying the code
              code = scannedJsonCode.code;
              promocode.value = scannedJsonCode.code;
              LogService.info(
                `Promocode: going to process scanned promocode: ${promocode.value}`
              );
              await applyPromocode();
              LogService.info(
                `Promocode: Done processing scanned promocode: ${promocode.value}`
              );
              scanPromocodeModal.value = false;
            } else {
              LogService.info(
                `Promocode: scanned value is not in the expected format`,
                { params: scannedJsonCode }
              );
              // Lets notify the user that code couldn't be scanned
              NotificationService.showCustomMessage(
                t("promocode.could-not-scan"),
                "error"
              );
            }
          } catch (err) {
            // Couldn't parse the scanned value into a json object
            LogService.info(
              `Promocode: scanned value is neither a url nor a valid json object. Checking if it's a simple token.`
            );

            const token = String(scannedValue).trim();
            if (token) {
              try {
                const results = await getPssVoucherByToken(
                  token,
                  POSConfigModule.groupId as number
                );

                if (results && results[0] && results[0].promocode) {
                  const voucherCode =
                    results[0].promocode.code ||
                    results[0].promocode.codeUnique ||
                    "";
                  LogService.info(
                    `Promocode: successfully retrieved voucher code from token: ${token}, code: ${voucherCode}`
                  );
                  promocode.value = voucherCode;
                  LogService.info(
                    `Promocode: going to process retrieved promocode: ${promocode.value}`
                  );
                  await applyPromocode();
                  LogService.info(
                    `Promocode: Done processing retrieved promocode: ${promocode.value}`
                  );
                  scanPromocodeModal.value = false;
                } else {
                  LogService.info(
                    `Promocode: no voucher code found for token: ${token}`
                  );
                  NotificationService.showCustomMessage(
                    t("promocode.could-not-scan"),
                    "error"
                  );
                }
              } catch (tokenErr) {
                LogService.info(
                  `Promocode Error: failed to retrieve voucher code from token: ${token}`,
                  { param: { token, tokenErr } },
                  LogLevel.Error
                );
                NotificationService.showCustomMessage(
                  t("promocode.could-not-scan"),
                  "error"
                );
              }
            }
          }
        }
      },
      { deep: true }
    );

    /**
     * Returns the last applied discount code (PROMOCODE, MEMBERSHIP_VOUCHER,
     * etc.) to prefill the input on mount. Vouchers (gift cards) are
     * intentionally excluded — they're a payment-side concept and shouldn't
     * be reused as the next promocode to apply.
     */
    function getExistingPromocode(): string {
      const lastOdCode = [...orderStore.selectedOrderSummary.orderDiscounts]
        .reverse()
        .find((od) => od.code && od.type !== DiscountType.VOUCHER);
      return lastOdCode?.code ? String(lastOdCode.code) : "";
    }

    async function removePromocode(code: string) {
      isLoading.value = true;

      try {
        await removeDiscount(code);
        promocode.value = "";
      } catch {
        // surfaced by removeDiscount
      } finally {
        isLoading.value = false;
      }
    }

    /**
     * Apply promocode function
     */
    async function applyPromocode() {
      isLoading.value = true;

      try {
        await applyDiscountCode(String(promocode.value));
        paymentType.value =
          getAppliedCodePaymentType(promocode.value) ?? paymentType.value;
      } catch {
        // surfaced by applyDiscountCode
      } finally {
        isLoading.value = false;
      }
    }

    return {
      promocode,
      isLoading,
      editPenIconStyle,
      editPenIconContent,
      editPenIconAlt,
      isPromocodeLengthValid,
      t,
      paymentType,
      updatePaymentType,
      PaymentMethods,
      inputPlaceholder,
      scanPromocodeModal,
      appliedCodesStats,
      currency,
      applyPromocode,
      removePromocode,
    };
  },
  methods: {
    openScanPromocodeModal() {
      this.scanPromocodeModal = true;
    },
  },
});
</script>

<style lang="scss" scoped>
@use "@/styles/variables.scss" as *;

.promocode-wrapper {
  // padding-top: 80px;
  .btn-height {
    height: 50px;
  }

  .inputs-wrapper {
    width: 80%;

    .scan-promocode-button {
      display: block;
      width: 96%;
      height: 80px;
      margin: 0 auto;
      padding: 0 40px;
      background-color: var(--primary-smeetz);
      color: white;
    }

    .scanner-divider {
      margin: 0 auto;
      width: 96%;
    }
  }

  .promocode-discount-result-wrapper {
    margin-top: 50px;
    width: 80%;

    .success-text {
      width: 90%;
      margin-left: auto;
      margin-right: auto;
      margin-bottom: 20px;
      color: #008000;
      font-weight: 600;
    }
    .discount-text {
      width: 90%;
      margin-left: auto;
      margin-right: auto;
      height: 110px;
      border-radius: 6px;
      background-color: #e5e5e5;
      font-size: 40px;
      color: $color-dark-grey;
    }
  }

  .options-wrapper {
    padding-bottom: 30px;
  }
}

.remaining-amount {
  margin-left: 20px;
}

.remaining-amount-text {
  font-size: 0.9rem;
  font-weight: bolder;
  background-color: $color-background-green;
  border-radius: 5px;
  padding: 3px 15px;
}

.scan-promocode-modal .content {
  padding-bottom: 30px;
}
</style>
